import { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';
import { requestPersistentStorage } from '../lib/idb';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Ask the OS to keep our IndexedDB safe from eviction (durability).
    requestPersistentStorage().catch(() => { /* harmless */ });

    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me')
        .then(response => {
          setUser(response.data);
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (phone, password, rememberMe = false) => {
    const response = await api.post('/auth/login', { phone, password, remember_me: !!rememberMe });
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    setUser(userData);
    return userData;
  };

  const signup = async (name, phone, password, inviteCode = '') => {
    const response = await api.post('/auth/signup', {
      name, phone, password,
      invite_code: inviteCode || undefined,
    });
    // Signup no longer returns token - account is pending approval
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  // iter195 — sign in via passkey (Face ID / Touch ID / Windows Hello).
  // Server returns the SAME { token, user } shape so the caller doesn't care.
  const loginWithPasskey = async ({ phone, rememberMe = true } = {}) => {
    const { authenticateWithPasskey } = await import('../lib/passkey');
    const { token, user: userData } = await authenticateWithPasskey({ phone, rememberMe });
    localStorage.setItem('token', token);
    setUser(userData);
    return userData;
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return response.data;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithPasskey, signup, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
